import React, { useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { useGetOffersQuery } from "@app/store/offersApi";
import { BlogShareToggle } from "@widgets/blog-share-toggle";
import { BlogWidget } from "@widgets/blog-widget";
import { BlogShare } from "@features/blog";
import { getFormattedDateParts } from "@entities/blog";
import { BlogOffers } from "@entities/blog/ui/blog-offers/blog-offers";
import { RouterConfig } from "@shared/lib/routerConfig";
import { Breadcrumbs } from "@shared/ui/Breadcrumbs";
import { SpriteIcon } from "@shared/ui/SpriteIcon";
import { BlogPostScreenProps } from "./types";
import classes from "./BlogPostScreen.module.scss";

const BlogPostScreen: React.FC<BlogPostScreenProps> = ({ post, searchTerms }) => {
    const { date: formattedDate } = getFormattedDateParts(post?.date);

    const { data } = useGetOffersQuery({
        page: 1,
        brandUuid: searchTerms.mark,
        modelUuid: searchTerms.model,
        generationUuid: searchTerms.generation,
        sort: "created_at@desc"
    });

    const shouldShowBlogOffers = post && data?.offers?.length > 0;
    const blogOffers = data?.offers?.slice(0, 3) ?? [];

    const authorSlug = post?._embedded?.author?.[0]?.slug ?? "";
    const authorHref = `${RouterConfig.AUTHORS}/${authorSlug}`;

    useEffect(() => {
        if (post && post?.id) {
            fetch(`https://cms.likvi.com/wp-json/post-views-counter/view-post/${post.id}`, {
                method: "POST"
            });
        }
    }, [post]);

    useEffect(() => {
        let newFilters: any[] = [];

        if (searchTerms) {
            if (searchTerms.mark) {
                newFilters.push({
                    match_phrase: {
                        mark: searchTerms.mark
                    }
                });
            }
            if (searchTerms.model) {
                newFilters.push({
                    match_phrase: {
                        model: searchTerms.model
                    }
                });
            }
            if (searchTerms.generation) {
                newFilters.push({
                    bool: {
                        should: [
                            {
                                term: {
                                    generation_number: searchTerms.generation
                                }
                            }
                        ]
                    }
                });
            }
        }
    }, [searchTerms]);

    return (
        <>
            <div className={classes.post}>
                <Breadcrumbs
                    className={classes.post__breadcrumbs}
                    prevItems={[{ href: RouterConfig.BLOG, text: "Блог" }]}
                    title={post?.title?.rendered || ""}
                />
                {!post && (
                    <div className={classes.NotFound}>
                        <h1>Ой! Такой страницы на нашем сайте нет :(</h1>
                        <p>
                            Наверное, вы ошиблись при наборе адреса или перешли по неверной ссылке.
                        </p>
                    </div>
                )}
                {post && (
                    <>
                        <Link
                            className={classes.post__close}
                            href={RouterConfig.BLOG}
                            aria-label="Вернуться в блог"
                            title="Вернуться в блог"
                        >
                            <SpriteIcon
                                className={classes["post__close--icon"]}
                                section={"blog"}
                                name={"close"}
                            />
                        </Link>
                        <BlogShare className={classes.post__share} />
                        <div className={classes.post__inner}>
                            <h1 className={classes.post__title}>{post?.title?.rendered}</h1>
                            <div className={classes.post__line}>
                                <div className={classes.post__details}>
                                    {formattedDate}
                                    <div className={classes.post__views}>
                                        <SpriteIcon
                                            className={classes["post__views--icon"]}
                                            section={"blog"}
                                            name={"views"}
                                        />
                                        {post?.views}
                                    </div>
                                </div>
                                <BlogShareToggle />
                            </div>
                            <img
                                className={classes.post__image}
                                src={
                                    post?.x_featured_media_large ??
                                    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mO8f/d+PQAIWgMcDMOFRwAAAABJRU5ErkJggg=="
                                }
                                alt={post?.title?.rendered}
                            />
                            <div
                                className={classes.post__article}
                                dangerouslySetInnerHTML={{
                                    __html: post?.content?.rendered
                                }}
                            />
                            <div className={classes.post__author}>
                                <Image
                                    className={classes["post__author--avatar"]}
                                    src={post?.x_gravatar}
                                    alt={post?.x_author ?? "Автор статьи"}
                                    width={57}
                                    height={57}
                                />
                                <div className={classes["post__author--info"]}>
                                    <Link
                                        className={classes["post__author--name"]}
                                        href={authorHref}
                                    >
                                        {post?.x_author}
                                    </Link>
                                    <span>Автор статьи</span>
                                </div>
                            </div>
                        </div>
                    </>
                )}
                {shouldShowBlogOffers && <BlogOffers offers={blogOffers} />}
            </div>
            <BlogWidget title={"Читайте также"} id={post?.id ?? 0} />
        </>
    );
};

export default BlogPostScreen;